import { Container, Graphics, Point, Rectangle, Sprite, Texture } from "pixi.js"
import { RainConfig, TileSize } from "game-common/models"
import { TileMapMetaProvider } from "game-common/tile_map_meta_provider"
import { isRainCoveredAt, rainEnvelope } from "game-common/weather"

interface Drop { sprite: Sprite; splash: Sprite; x: number; y: number; age: number; active: boolean }

/** Fixed pools, viewport-only emission, no filters, map scans, physics or server particles. */
export class Rain {
    static readonly MAX_DROPS = 240
    readonly container = new Container()
    readonly sky = new Container()
    private drops: Drop[] = []
    private flash = new Sprite(Texture.WHITE)
    private bolt = new Graphics()
    private config?: RainConfig
    private anchor = 0
    private lastAt = 0
    private roomId?: string
    private debt = 0
    private cursor = 0
    private activeCount = 0
    // Smoothed cloud envelope also drives vegetation during the lead-in before the first drops.
    stormWind = 0
    private intensity = 0
    private lightningAt = 0
    private flashAt = -Infinity
    private thunderAt = Infinity
    private probe = new Point()
    private topLeft = new Point()
    private bottomRight = new Point()

    private overlapsRoof = (sprite: Sprite, provider: TileMapMetaProvider): boolean => {
        const left = sprite.x - sprite.anchor.x * sprite.width
        const top = sprite.y - sprite.anchor.y * sprite.height
        const right = left + sprite.width, bottom = top + sprite.height
        // Check the full texture footprint, including streak tails and expanding splashes.
        // Sampling at most one tile apart also handles textures larger than a tile.
        const columns = Math.max(1, Math.ceil(sprite.width / TileSize))
        const rows = Math.max(1, Math.ceil(sprite.height / TileSize))
        for (let column = 0; column <= columns; column++) {
            for (let row = 0; row <= rows; row++) {
                if (isRainCoveredAt(provider, {
                    x: left + (right - left) * column / columns,
                    y: top + (bottom - top) * row / rows,
                })) return true
            }
        }
        return false
    }

    constructor(private surface: Container, private screen: Rectangle,
        textures: { drop: Texture; splash: Texture },
        private thunder: () => void, private stopThunder: () => void) {
        this.container.zIndex = 1000001
        this.sky.zIndex = 1000002
        for (let i = 0; i < Rain.MAX_DROPS; i++) {
            const sprite = new Sprite(textures.drop), splash = new Sprite(textures.splash)
            sprite.anchor.set(0.5, 1); splash.anchor.set(0.5)
            sprite.visible = splash.visible = false
            this.container.addChild(sprite, splash)
            this.drops.push({ sprite, splash, x: 0, y: 0, age: 0, active: false })
        }
        this.flash.tint = 0xcbdfff
        this.sky.addChild(this.flash, this.bolt)
        this.container.visible = this.sky.visible = false
    }

    update = (config: RainConfig | null, provider: TileMapMetaProvider, now = performance.now()) => {
        const dt = Math.min(0.05, Math.max(0, (now - this.lastAt) / 1000))
        this.lastAt = now
        const sceneId = provider?.twoStoreyHouse?.groundRoomId ?? provider?.roomId
        if (sceneId !== this.roomId) {
            this.roomId = sceneId
            this.config = undefined; this.intensity = 0; this.stormWind = 0; this.debt = 0; this.activeCount = 0
            this.thunderAt = Infinity; this.flashAt = -Infinity; this.stopThunder()
            for (const drop of this.drops) { drop.active = false; drop.sprite.visible = drop.splash.visible = false }
        }
        if ((config ?? undefined) !== this.config) {
            const newStorm = config?.id !== this.config?.id
            this.config = config ?? undefined; this.anchor = now
            if (newStorm) { this.lightningAt = now + 10000; this.thunderAt = Infinity }
        }
        const target = config ? rainEnvelope(config, config.elapsedMs + now - this.anchor) : { rain: 0, cloud: 0 }
        // Also smooth mid-storm clear commands and config removal.
        const step = 1 - Math.exp(-dt / 0.65)
        this.intensity += (target.rain - this.intensity) * step
        this.stormWind += (target.cloud - this.stormWind) * step
        this.container.visible = this.intensity > 0.001 || this.activeCount > 0
        this.sky.visible = this.intensity > 0.001 || now - this.flashAt < 320
        if (!provider || (!this.container.visible && !this.sky.visible)) return
        this.probe.set(this.screen.x, this.screen.y)
        this.surface.toLocal(this.probe, undefined, this.topLeft)
        this.probe.set(this.screen.right, this.screen.bottom)
        this.surface.toLocal(this.probe, undefined, this.bottomRight)
        const left = Math.min(this.topLeft.x, this.bottomRight.x) - 64
        const top = Math.min(this.topLeft.y, this.bottomRight.y) - 64
        const width = Math.abs(this.bottomRight.x - this.topLeft.x) + 128
        const height = Math.abs(this.bottomRight.y - this.topLeft.y) + 128
        // Sky effects use screen coordinates on an unmasked layer below the HUD.
        // Ground visibility should not erase lightning flashes at night.
        const skyLeft = this.screen.x - 64, skyTop = this.screen.y - 32
        const skyWidth = this.screen.width + 128, skyHeight = this.screen.height + 64
        this.debt = Math.min(12, this.debt + dt * 190 * this.intensity)
        while (this.debt >= 1) {
            this.debt--
            const drop = this.drops[this.cursor++ % this.drops.length]
            if (drop.active) continue
            const x = left + Math.random() * width, y = top + Math.random() * height
            if (x < 0 || y < 0 || x >= provider.width() * TileSize || y >= provider.height() * TileSize) continue
            // Coverage is independent of cutaway alpha: sheltered tiles emit no rain effects.
            if (isRainCoveredAt(provider, { x, y })) continue
            drop.x = x; drop.y = y
            drop.age = 0; drop.active = true; this.activeCount++; drop.sprite.visible = true; drop.splash.visible = false
        }
        for (const drop of this.drops) {
            if (!drop.active) continue
            drop.age += dt
            const fallSeconds = 0.42
            if (drop.age < fallSeconds) {
                const lift = (fallSeconds - drop.age) * 620
                drop.sprite.position.set(drop.x - lift * 0.18, drop.y - lift)
                drop.sprite.alpha = 0.5 * Math.min(1, drop.age / 0.08)
                drop.sprite.visible = !this.overlapsRoof(drop.sprite, provider)
            } else {
                drop.sprite.visible = false
                const t = (drop.age - fallSeconds) / 0.22
                drop.splash.visible = t < 1
                drop.splash.position.set(drop.x, drop.y)
                drop.splash.scale.set(0.5 + t, 0.5 + t)
                drop.splash.alpha = Math.max(0, (1 - t) * 0.45)
                if (drop.splash.visible) drop.splash.visible = !this.overlapsRoof(drop.splash, provider)
                if (t >= 1) { drop.active = false; this.activeCount-- }
            }
        }
        if (config && !config.clearing && this.intensity > 0.35 && now >= this.lightningAt) {
            this.lightningAt = now + 6500 + Math.random() * 5000
            this.flashAt = now; this.thunderAt = now + 700 + Math.random() * 900
            const x = skyLeft + skyWidth * (0.2 + Math.random() * 0.6)
            this.bolt.clear().lineStyle(2, 0xdbeaff, 0.8).moveTo(x, skyTop)
            for (let i = 1; i <= 6; i++) this.bolt.lineTo(x + (Math.random() - 0.5) * 60, skyTop + skyHeight * i / 7)
        }
        const flashAge = now - this.flashAt
        const flash = Math.max(0, 1 - flashAge / 180) * 0.22 + (flashAge > 220 ? Math.max(0, 1 - (flashAge - 220) / 100) * 0.1 : 0)
        this.flash.position.set(skyLeft, skyTop); this.flash.width = skyWidth; this.flash.height = skyHeight; this.flash.alpha = flash
        this.bolt.alpha = Math.max(0, 1 - flashAge / 180)
        if (now >= this.thunderAt) { this.thunderAt = Infinity; if (this.intensity > 0.1) this.thunder() }
    }

    static textures = (renderer: { generateTexture: (g: Graphics) => Texture }) => {
        const bake = (draw: (g: Graphics) => void) => {
            const g = new Graphics(); draw(g); const texture = renderer.generateTexture(g); g.destroy(); return texture
        }
        return {
            drop: bake(g => { g.lineStyle(1, 0xbdd7ef, 0.85).moveTo(0, 0).lineTo(3, 16) }),
            splash: bake(g => { g.lineStyle(1, 0xc1dcf1, 0.8).drawEllipse(0, 0, 4, 1.5) }),

        }
    }
}
